/**
 * <MatrixScreensaver />
 * ─────────────────────────────────────────────────────────────────────
 * Privacy screensaver for LOGGED-IN users only. After `idle_minutes`
 * (admin-configurable, default 15) of no input, the viewport is covered
 * with a Matrix-movie style rain — but in the platform's signature
 * violet — where every falling column spells a real wisdom proverb in
 * one of 13 languages (readable top → bottom in its native script).
 *
 * Any input (mouse, key, touch, wheel) dismisses the overlay and puts
 * the user right back where they were. No session lock.
 *
 * Admin-tunable via /dashboard/admin/screensaver — settings live in the
 * `screensaver_config` collection and are fetched once per login.
 * Window events keep the mounted watcher in sync without a reload:
 *   've:screensaver:config'  detail = new config  (after admin save)
 *   've:screensaver:preview' activate immediately (admin "Preview now")
 */
import React, { useEffect, useRef, useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useBranding } from '@/context/BrandingContext';
import api from '@/lib/api';
import { QUOTE_POOL } from '@/lib/wisdomQuotes';

export const SCREENSAVER_PRESET_COLORS = {
  violet: '#AE52EA',
  gold: '#D4AF37',
  green: '#00FF41',
  cyan: '#22D3EE',
  crimson: '#EF4444',
  ice: '#E8ECF3',
};

export function resolveScreensaverColor(cfg) {
  if (!cfg) return SCREENSAVER_PRESET_COLORS.violet;
  if (cfg.color_preset === 'custom') return cfg.custom_color || SCREENSAVER_PRESET_COLORS.violet;
  return SCREENSAVER_PRESET_COLORS[cfg.color_preset] || SCREENSAVER_PRESET_COLORS.violet;
}

const SPEED_STEP_MS = { calm: 96, normal: 62, fast: 38 };

/* Wake events — anything a human does resets/dismisses. */
const ACTIVITY_EVENTS = ['pointermove', 'pointerdown', 'keydown', 'wheel', 'touchstart'];

export default function MatrixScreensaver() {
  const { user } = useAuth();
  const [config, setConfig] = useState(null);
  const [active, setActive] = useState(false);
  const activeRef = useRef(false);
  const lastActiveRef = useRef(Date.now());
  const activatedAtRef = useRef(0);
  useEffect(() => { activeRef.current = active; }, [active]);

  /* ── Config fetch (on login) + live admin sync events ─────────────── */
  useEffect(() => {
    if (!user) { setConfig(null); setActive(false); return undefined; }
    let cancelled = false;
    api.get('/screensaver/config')
      .then(({ data }) => { if (!cancelled) setConfig(data); })
      .catch(() => { /* screensaver is best-effort — stay dormant */ });
    const onCfg = (e) => setConfig(e.detail);
    const onPreview = () => { activatedAtRef.current = Date.now(); setActive(true); };
    window.addEventListener('ve:screensaver:config', onCfg);
    window.addEventListener('ve:screensaver:preview', onPreview);
    return () => {
      cancelled = true;
      window.removeEventListener('ve:screensaver:config', onCfg);
      window.removeEventListener('ve:screensaver:preview', onPreview);
    };
  }, [user]);

  /* ── Idle watcher ──────────────────────────────────────────────────── */
  useEffect(() => {
    if (!user || !config?.enabled) return undefined;
    const touch = () => { lastActiveRef.current = Date.now(); };
    touch();
    ACTIVITY_EVENTS.forEach((ev) => window.addEventListener(ev, touch, { passive: true }));
    const idleMs = Math.max(1, config.idle_minutes || 15) * 60_000;
    const timer = setInterval(() => {
      // Never re-arm while already showing — re-stamping activatedAt
      // would keep the wake-grace window open and swallow wake events.
      if (activeRef.current || document.hidden) return;
      if (Date.now() - lastActiveRef.current >= idleMs) {
        activatedAtRef.current = Date.now();
        setActive(true);
      }
    }, 5_000);
    return () => {
      ACTIVITY_EVENTS.forEach((ev) => window.removeEventListener(ev, touch));
      clearInterval(timer);
    };
  }, [user, config]);

  /* ── Wake — any input dismisses (grace 600ms so the activation
        doesn't instantly self-dismiss from queued events) ───────────── */
  useEffect(() => {
    if (!active) return undefined;
    const wake = () => {
      if (Date.now() - activatedAtRef.current < 600) return;
      lastActiveRef.current = Date.now();
      setActive(false);
    };
    ACTIVITY_EVENTS.forEach((ev) => window.addEventListener(ev, wake, { passive: true }));
    return () => ACTIVITY_EVENTS.forEach((ev) => window.removeEventListener(ev, wake));
  }, [active]);

  if (!user || !active || !config) return null;
  return <ScreensaverOverlay config={config} />;
}

/* ═══════════════════════ Overlay ═══════════════════════ */

function ScreensaverOverlay({ config }) {
  const { branding } = useBranding() || {};
  const color = resolveScreensaverColor(config);
  return (
    <div
      className="fixed inset-0 z-[9999] bg-black cursor-none select-none"
      data-testid="screensaver-overlay"
      aria-label="Screensaver — move the mouse or press a key to return"
    >
      <RainCanvas color={color} speed={config.speed} />
      {(config.show_brand || config.show_clock) && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div
            className="text-center px-10 py-8 rounded-lg"
            style={{
              background: 'rgba(0,0,0,0.55)',
              backdropFilter: 'blur(6px)',
              border: `1px solid ${hexA(color, 0.28)}`,
              boxShadow: `0 0 80px -20px ${hexA(color, 0.45)}`,
            }}
          >
            {config.show_brand && (
              <div
                className="text-[11px] uppercase tracking-[0.42em] font-bold font-mono"
                style={{ color: '#D4AF37' }}
                data-testid="screensaver-brand"
              >
                {(branding?.brand_name || 'Vigilant Entities')}
              </div>
            )}
            {config.show_clock && <Clock color={color} />}
            {config.show_brand && (
              <div className="mt-3 text-[9px] uppercase tracking-[0.3em] font-mono text-white/40">
                Protected session · move to return
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function Clock({ color }) {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);
  const hh = String(now.getHours()).padStart(2, '0');
  const mm = String(now.getMinutes()).padStart(2, '0');
  return (
    <div className="mt-2" data-testid="screensaver-clock">
      <span
        className="text-6xl font-bold tabular-nums"
        style={{ fontFamily: 'Georgia, "Times New Roman", serif', color: '#FFFFFF', textShadow: `0 0 24px ${hexA(color, 0.55)}` }}
      >
        {hh}
        <span className="opacity-60">:</span>
        {mm}
      </span>
      <div className="mt-1 text-[10px] uppercase tracking-[0.28em] font-mono text-white/50">
        {now.toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'long' })}
      </div>
    </div>
  );
}

/* ═══════════════════════ Rain canvas ═══════════════════════ */

function RainCanvas({ color, speed }) {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext('2d');
    const FONT = 18;
    const COL_W = FONT * 1.2;
    let w = window.innerWidth, h = window.innerHeight;
    let cols = [];

    const newColumn = (x) => {
      const q = QUOTE_POOL[Math.floor(Math.random() * QUOTE_POOL.length)];
      // The proverb's characters fall in order, so reading the column
      // top → bottom gives the whole line in its native script.
      const chars = [...q.text.replace(/\s+/g, ' ')];
      return {
        x,
        y: -Math.floor(Math.random() * (h / FONT) * 1.6),  // stagger entries above the fold
        qi: 0,
        chars,
        trail: chars.length + 4,
        mult: 0.75 + Math.random() * 0.7,   // organic per-column pace
        acc: Math.random() * 120,           // desync starts
      };
    };

    const setSize = () => {
      w = window.innerWidth; h = window.innerHeight;
      canvas.width = w * window.devicePixelRatio;
      canvas.height = h * window.devicePixelRatio;
      canvas.style.width = `${w}px`; canvas.style.height = `${h}px`;
      ctx.setTransform(window.devicePixelRatio, 0, 0, window.devicePixelRatio, 0, 0);
      const n = Math.ceil(w / COL_W);
      cols = Array.from({ length: n }, (_, i) => newColumn(i * COL_W + COL_W / 2));
    };
    setSize();
    window.addEventListener('resize', setSize);

    const stepMs = SPEED_STEP_MS[speed] || SPEED_STEP_MS.normal;
    let last = performance.now();
    let raf;
    let paused = false;
    const onVis = () => { paused = document.hidden; };
    document.addEventListener('visibilitychange', onVis);

    const frame = (t) => {
      raf = requestAnimationFrame(frame);
      if (paused) { last = t; return; }
      const dt = Math.min(t - last, 200);
      last = t;

      ctx.fillStyle = '#000';
      ctx.fillRect(0, 0, w, h);
      ctx.font = `${FONT}px "SF Mono", "Noto Sans", Menlo, Consolas, monospace`;
      ctx.textAlign = 'center';

      cols.forEach((c, i) => {
        // Advance the head on this column's own clock.
        c.acc += dt;
        const need = stepMs / c.mult;
        while (c.acc >= need) {
          c.acc -= need;
          c.y += 1;
          c.qi += 1;
        }

        // Deterministic redraw: head glyph bright white with a coloured
        // glow; the full proverb trails above it in the theme colour,
        // fading gently — long enough to READ the whole line.
        for (let d = 0; d < c.trail; d++) {
          const idx = c.qi - d;
          if (idx < 0) break;
          const row = c.y - d;
          const py = row * FONT;
          if (py < -FONT) break;          // rows above are even higher — stop
          if (py > h + FONT) continue;    // below the fold — keep walking up
          const ch = c.chars[idx % c.chars.length];
          if (ch === ' ') continue;
          if (d === 0) {
            ctx.globalAlpha = 1;
            ctx.shadowColor = color;
            ctx.shadowBlur = 12;
            ctx.fillStyle = '#FFFFFF';
            ctx.fillText(ch, c.x, py);
            ctx.shadowBlur = 0;
          } else {
            ctx.globalAlpha = Math.max(0.08, (1 - d / c.trail)) * 0.92;
            ctx.fillStyle = color;
            ctx.fillText(ch, c.x, py);
          }
        }
        ctx.globalAlpha = 1;

        // Entire trail below the fold → recycle with a fresh proverb.
        if ((c.y - c.trail) * FONT > h) {
          cols[i] = newColumn(c.x);
          cols[i].y = -Math.floor(Math.random() * 30) - 2;
        }
      });
    };
    raf = requestAnimationFrame(frame);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', setSize);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [color, speed]);

  return <canvas ref={canvasRef} className="absolute inset-0" data-testid="screensaver-canvas" aria-hidden="true" />;
}

/* hex "#RRGGBB" → rgba() string at given alpha */
function hexA(hex, a) {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex || '');
  if (!m) return `rgba(174,82,234,${a})`;
  const n = parseInt(m[1], 16);
  return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${a})`;
}
